import { useEffect, useState } from "react";
import { api, fmtEur, fmtDateTime } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Coins, Receipt as ReceiptIcon, CalendarBlank, ArrowRight, ChartBar, Percent, TrendUp, Wallet } from "@phosphor-icons/react";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";

const DAY_LBLS = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sá", "Do"];

function buildDailyChart(services, year, month) {
  // month: 1-12
  const lastDay = new Date(year, month, 0).getDate();
  const arr = Array.from({ length: lastDay }, (_, i) => {
    const d = i + 1;
    return { day: d, label: String(d).padStart(2, "0"), count: 0, neto: 0 };
  });
  for (const s of services) {
    const dt = new Date(s.date);
    if (dt.getFullYear() !== year || dt.getMonth() + 1 !== month) continue;
    const idx = dt.getDate() - 1;
    if (idx < 0 || idx >= arr.length) continue;
    arr[idx].count += 1;
    arr[idx].neto += s.colaborador_neto || 0;
  }
  return arr;
}

export default function ColaboradorHome() {
  const { user, org } = useAuth();
  const [stats, setStats] = useState(null);
  const [services, setServices] = useState([]);
  const [appts, setAppts] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, sv, a] = await Promise.allSettled([
        api.get("/dashboard/colaborador"),
        api.get("/services"),
        api.get("/appointments"),
      ]);
      setStats(s.status === "fulfilled" ? s.value.data : { month: "", services_count: 0, bruto: 0, iva: 0, retencion: 0, neto: 0 });
      setServices(sv.status === "fulfilled" ? sv.value.data : []);
      setAppts(a.status === "fulfilled" ? a.value.data : []);
    })();
  }, []);

  if (!stats) return <div className="grid grid-cols-1 md:grid-cols-3 gap-6">{["a","b","c"].map((k)=><Skeleton key={`sk-${k}`} className="h-32 rounded-sm" />)}</div>;

  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const dailyData = buildDailyChart(services, year, month);
  const pctColab = org?.commission_colaborador ?? 60;
  const pctTitular = org?.commission_titular ?? 40;
  const retIRPF = user?.retention_irpf_rate ?? 15;

  const maxCount = Math.max(1, ...dailyData.map((d) => d.count));

  return (
    <div className="space-y-8">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Tu mes · {stats.month}</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter mt-1">Hola, {user.name.split(" ")[0]}</h1>
        <p className="text-sm text-gray-600 mt-2">Aquí tienes tu saldo acumulado, tu volumen y la transparencia de tu reparto.</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="metric-card p-6 lg:col-span-2 bg-[#111827] text-white border-[#111827]" data-testid="colab-liquido">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">Líquido acumulado este mes</div>
            <Coins size={20} weight="duotone" className="text-emerald-400" />
          </div>
          <div className="mt-3 font-display font-bold text-5xl tracking-tighter tabular-nums text-emerald-400">{fmtEur(stats.neto)}</div>
          <div className="mt-3 text-xs text-[var(--ag-text)]/60 tabular-nums">
            Base: {fmtEur(stats.bruto)} · +IVA {fmtEur(stats.iva)} · −Retención {fmtEur(stats.retencion)}
          </div>
        </div>
        <div className="metric-card p-6" data-testid="colab-servicios">
          <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">Servicios realizados</div>
          <div className="mt-3 font-display font-bold text-5xl tracking-tighter tabular-nums text-[var(--ag-gold)]">{stats.services_count}</div>
          <Link to="/services" className="text-xs text-[var(--ag-gold)] flex items-center gap-1 mt-3">Ver detalle <ArrowRight size={12} weight="bold" /></Link>
        </div>
      </div>

      {/* NEW · Panel económico transparente */}
      <section className="bg-white border border-gray-200 rounded-sm p-5" data-testid="colab-econ-panel">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500 flex items-center gap-2"><ChartBar weight="duotone" /> Tu economía este mes</div>
            <h2 className="font-display text-2xl font-bold tracking-tighter mt-1">Transparencia 100%</h2>
            <p className="text-xs text-gray-500 mt-1">Volumen diario · reparto pactado con el titular · líquido que vas a facturar.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Tu % pactado</div>
              <div className="font-display font-black text-2xl tabular-nums text-[var(--ag-gold)]" data-testid="colab-pct-pactado">{pctColab}%</div>
            </div>
            <div className="text-right border-l border-gray-200 pl-3">
              <div className="text-[10px] uppercase tracking-wider text-gray-500">Titular</div>
              <div className="font-display text-xl tabular-nums text-[var(--ag-text-muted)]">{pctTitular}%</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Bar chart wider */}
          <div className="lg:col-span-3 h-56" data-testid="colab-bar-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dailyData} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
                <CartesianGrid strokeDasharray="2 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#94A3B8" }} interval={2} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} width={40} />
                <Tooltip
                  cursor={{ fill: "rgba(255,183,3,0.07)" }}
                  contentStyle={{ background: "#1F1F1F", border: "1px solid #2A2A2A", borderRadius: 4, fontSize: 12, color: "#fff" }}
                  formatter={(value, name, p) => name === "neto" ? [fmtEur(value), "Líquido"] : [value, "Servicios"]}
                  labelFormatter={(l) => `Día ${l}`}
                />
                <Bar dataKey="count" radius={[3, 3, 0, 0]}>
                  {dailyData.map((d, i) => (
                    <Cell key={`d-${d.day || i}`} fill={d.count >= maxCount * 0.66 ? "#FFB703" : d.count >= maxCount * 0.33 ? "#06B6D4" : "#475569"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          {/* KPIs lateral */}
          <div className="space-y-3">
            <div className="metric-card p-4">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-gray-500"><TrendUp size={11} weight="bold" /> Base bruta</div>
              <div className="font-display font-bold text-2xl tabular-nums mt-1">{fmtEur(stats.bruto)}</div>
              <div className="text-[10px] text-gray-500 mt-0.5">tras tu {pctColab}% sobre los servicios</div>
            </div>
            <div className="metric-card p-4">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-gray-500"><Percent size={11} weight="bold" /> Retención IRPF</div>
              <div className="font-display font-bold text-2xl tabular-nums mt-1 text-amber-400">−{fmtEur(stats.retencion)}</div>
              <div className="text-[10px] text-gray-500 mt-0.5">{retIRPF}% sobre base</div>
            </div>
            <div className="metric-card p-4 bg-emerald-500/10 border-emerald-500/20">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-emerald-400"><Wallet size={11} weight="bold" /> A facturar</div>
              <div className="font-display font-bold text-3xl tabular-nums mt-1 text-emerald-400" data-testid="colab-a-facturar">{fmtEur(stats.neto)}</div>
              <div className="text-[10px] text-gray-500 mt-0.5">líquido fin de mes</div>
            </div>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500 flex items-center gap-2"><ReceiptIcon weight="duotone" /> Últimos servicios</div>
            <Link to="/services"><Button variant="ghost" size="sm" className="rounded-sm">Ver todos</Button></Link>
          </div>
          <div className="space-y-2">
            {services.slice(0, 5).map((s) => (
              <div key={s.id} className="flex items-center justify-between text-sm py-2 border-b border-gray-100 last:border-0">
                <div>
                  <div className="font-mono-tab font-semibold">{s.license_plate}</div>
                  <div className="text-xs text-gray-500">{fmtDateTime(s.date)} · {s.wash_type}</div>
                </div>
                <div className="text-right">
                  <div className="tabular-nums font-semibold">{fmtEur(s.colaborador_neto)}</div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">{s.status}</div>
                </div>
              </div>
            ))}
            {services.length === 0 && <div className="text-sm text-gray-500 py-4 text-center">Aún sin servicios este mes</div>}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-[0.2em] text-gray-500 flex items-center gap-2"><CalendarBlank weight="duotone" /> Tu agenda</div>
            <Link to="/calendar"><Button variant="ghost" size="sm" className="rounded-sm">Calendario</Button></Link>
          </div>
          <div className="space-y-2">
            {appts.slice(0, 5).map((a)=>(
              <div key={a.id} className="flex items-center justify-between text-sm py-2 border-b border-gray-100 last:border-0">
                <div>
                  <div className="font-medium">{a.license_plate} · {a.wash_type}</div>
                  <div className="text-xs text-gray-500">{fmtDateTime(a.date)} · {a.customer_name || "—"}</div>
                </div>
              </div>
            ))}
            {appts.length === 0 && <div className="text-sm text-gray-500 py-4 text-center">No hay citas próximas</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
